/**
 * Modal popup that opens when the user clicks one of the amber guild
 * base pins on the world map. Surfaces the rich per-guild detail the
 * backend now ships with `/api/live/{slug}/bases`:
 *   - member list (top 8 by level) with last-online relative time
 *   - guild chest contents (top 6 items by count)
 *   - expedition progress
 *   - camp count (built vs. cap)
 *
 * Deliberately read-only — admins can act on this via the existing
 * Admin → Player Search / Server Config flows.
 */
import { X, Crown, Users, Package, Compass, Tent } from "lucide-react";

// Palworld stores LastOnlineTicks as a Windows FILETIME-ish 100-ns count
// (10_000_000 ticks per second), but it's relative to the GAME world's
// elapsed time, not a real clock. To stay safe, render it as ranked
// activity (newest first) rather than an absolute date.
function ticksToRelativeRank(members) {
  const sorted = [...members].sort((a, b) => (b.last_online_ticks || 0) - (a.last_online_ticks || 0));
  const ranks = new Map();
  sorted.forEach((m, i) => ranks.set(m.name, i));
  return ranks;
}

export function GuildBasePopup({ guild, onClose }) {
  if (!guild) return null;
  const members = guild.members || [];
  const activityRank = ticksToRelativeRank(members);
  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="guild-base-popup"
    >
      <div
        className="glass-card max-w-md w-full max-h-[80dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <h2 className="text-2xl mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }} data-testid="guild-popup-name">
              {guild.guild_name}
            </h2>
            <div className="text-xs text-[var(--muted)] flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1"><Crown size={11}/> {guild.admin_name || "—"}</span>
              <span>·</span>
              <span>Guild Lv <span className="glow-teal">{guild.guild_level}</span></span>
              <span>·</span>
              <span>{guild.member_count} member{guild.member_count === 1 ? "" : "s"}</span>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-sm btn-secondary" data-testid="guild-popup-close">
            <X size={14}/>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Members */}
          <section data-testid="guild-popup-members">
            <h3 className="label inline-flex items-center gap-2"><Users size={11}/> Members</h3>
            <ul className="space-y-1">
              {members.slice(0, 8).map((m) => (
                <li key={m.name} className="flex items-center justify-between bg-black/30 px-3 py-1.5 rounded text-sm">
                  <span className="truncate">
                    {m.name === guild.admin_name && (
                      <Crown size={11} className="inline-block mr-1 text-[var(--gold)]" />
                    )}
                    {m.name}
                  </span>
                  <span className="text-xs text-[var(--muted)] shrink-0">
                    Lv <span className="glow-teal">{m.level}</span>
                    {activityRank.get(m.name) === 0 && members.length > 1 && (
                      <span className="ml-2 text-[var(--teal)]">· recent</span>
                    )}
                  </span>
                </li>
              ))}
              {members.length > 8 && (
                <li className="text-[10px] text-[var(--muted)] italic px-3">
                  …and {members.length - 8} more
                </li>
              )}
              {members.length === 0 && (
                <li className="text-[11px] text-[var(--muted)] italic px-3">No member data exported.</li>
              )}
            </ul>
          </section>

          {/* Camps + expeditions */}
          <section className="grid grid-cols-2 gap-3" data-testid="guild-popup-stats">
            <div className="bg-black/30 px-3 py-2 rounded">
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] flex items-center gap-1">
                <Tent size={10}/> Camps
              </div>
              <div className="text-lg glow-teal">{guild.camp_num} / {guild.camp_num_total}</div>
            </div>
            <div className="bg-black/30 px-3 py-2 rounded">
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] flex items-center gap-1">
                <Compass size={10}/> Expeditions
              </div>
              <div className="text-lg glow-teal">{guild.expeditions_finished}</div>
            </div>
          </section>

          {/* Guild chest */}
          {guild.chest_top?.length > 0 && (
            <section data-testid="guild-popup-chest">
              <h3 className="label inline-flex items-center gap-2"><Package size={11}/> Guild chest</h3>
              <ul className="space-y-1">
                {guild.chest_top.map((it) => (
                  <li key={it.item} className="flex justify-between bg-black/30 px-3 py-1.5 rounded text-sm">
                    <span className="truncate">{it.item}</span>
                    <span className="text-xs text-[var(--teal)]">× {it.count.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default GuildBasePopup;
